import { PharmacIconProps } from '@pharmac/ui'
import { useMemo } from 'react'
import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom'
import ExtranetRoot from '../components/extranet/ExtranetRoot'
import { ShopProductView } from '../components/shop-products/ShopProductView'
import { UpdateUserInfoForm } from '../components/user/UpdateUserInfoForm'
import { LogoutView } from '../views/LogoutView'
import { NotFoundPageView } from '../views/NotFoundPage'
import { UnauthorizedView } from '../views/UnauthorizedView'
import { CustomerGroupSettingsView } from '../views/customer-group-settings/CustomerGroupSettingsView'
import { CustomerGroupsView } from '../views/customer-groups/CustomerGroupsView'
import { CustomerReportsView } from '../views/customer-reports/CustomerReportsView'
import { CustomerInviteLinksView } from '../views/customer/CustomerInviteLinksView'
import { CustomerView } from '../views/customer/CustomerView'
import { DoseDispensingCatalogView } from '../views/dose-dispensing-catalog/DoseDispensingCatalogView'
import { ExpiredInviteLinkView } from '../views/expired-invite-link/ExpiredInviteLinkView'
import { FrontpageView } from '../views/frontpage/FrontpageView'
import { JoinCustomerView } from '../views/join-customer/JoinCustomerView'
import { MaintenanceBreakView } from '../views/maintenance-break/MaintenanceBreakView'
import { NewUserLandingView } from '../views/new-user-landing/NewUserLandingView'
import { NewsletterView } from '../views/newsletters/NewsletterView'
import { NewslettersView } from '../views/newsletters/NewslettersView'
import { OrderChangesView } from '../views/order-changes/OrderChangesView'
import { OrderHistoryView } from '../views/order-history/OrderHistoryView'
import { OrderSchedulesView } from '../views/order-schedules/OrderSchedulesView'
import { OrderTicketsView } from '../views/order-tickets/OrderTicketsView'
import { ProductShortagesView } from '../views/product-shortages/ProductShortagesView'
import { ServiceView } from '../views/services/ServiceView'
import { ServicesView } from '../views/services/ServicesView'
import { ShopRootView } from '../views/shop/ShopRootView'
import { ShopOrderHistory } from '../views/shop/order-history/ShopOrderHistory'
import { ContactView } from '../views/support/ContactView'
import { FaqsView } from '../views/support/FaqsView'
import { SupportRootView } from '../views/support/SupportRootView'
import { UserView } from '../views/user/UserView'
import { AuthorizedRoot, RouteProtectionFlags, useAuthorizationFlags } from './RouteGuards'
import { RouteKeys } from './i18n/extranet-routes'
import { localizeLinkPath } from './router-utils'

export type ExtranetRouteObject = Omit<RouteObject, 'children' | 'path'> & {
  name: string
  protection?: RouteProtectionFlags
  icon?: PharmacIconProps['icon']
  roles?: string[]
  children?: ExtranetRouteObject[]
  onlyRoute?: boolean
  path?: RouteKeys
}

export const extranetRoutes = (): ExtranetRouteObject[] => [
  {
    path: 'registration',
    name: '',
    element: <NewUserLandingView />,
  },
  {
    path: 'joinCustomerLogin',
    name: '',
    element: <JoinCustomerView />,
  },
  {
    path: 'invalidInvitation',
    name: '',
    element: <ExpiredInviteLinkView />,
  },
  {
    path: 'maintenanceBreak',
    name: '',
    element: <MaintenanceBreakView />,
  },
  {
    name: 'Root',
    path: 'root',
    element: <ExtranetRoot />,
    protection: {
      allowPharmacUser: true,
    },
    children: [
      {
        index: true,
        name: 'menu.home',
        icon: 'home',
        element: <FrontpageView />,
      },
      {
        path: 'services',
        name: 'menu.services',
        icon: 'doctorsBag',
        element: <Outlet />,
        protection: {
          pharmacyOrCareUnit: true,
        },
        children: [
          {
            index: true,
            name: 'menu.services',
            element: <ServicesView />,
          },
          {
            path: 'service',
            name: 'menu.service',
            element: <ServiceView />,
          },
        ],
      },
      /* @deprecated - 1.5 materials näkymä */
      /* {
        path: 'shop',
        name: 'menu.materials',
        icon: 'book',
        element: <MaterialsRootView />,
      },
      {
        path: 'product',
        name: '',
        onlyRoute: true,
        element: <MaterialProductView />,
      }, */
      /* kauppanäkymä tulee 1.5 päivitykseen */
      {
        path: 'shop',
        name: 'menu.shop',
        icon: 'book',
        element: <Outlet />,
        children: [
          {
            onlyRoute: true,
            index: true,
            path: 'shop.own',
            name: '',
            element: <ShopRootView activePage="own" />,
          },
          {
            onlyRoute: true,
            path: 'shop',
            name: '',
            element: <ShopRootView activePage="all" />,
          },
          {
            onlyRoute: true,
            path: 'shop.orderHistory',
            name: '',
            element: <ShopOrderHistory />,
          },
        ],
      },
      {
        path: 'product',
        name: '',
        onlyRoute: true,
        element: <ShopProductView />,
      },
      {
        path: 'own.product',
        name: '',
        onlyRoute: true,
        element: <ShopProductView />,
      },
      {
        path: 'support',
        name: 'menu.support',
        icon: 'helpCircle',
        element: <SupportRootView />,
        children: [
          {
            path: 'support.newsletters',
            name: 'menu.newsletters',
            icon: 'rss',
            element: <NewslettersView />,
          },
          {
            path: 'support.newsletter',
            onlyRoute: true,
            name: '',
            element: <NewsletterView />,
          },
          {
            path: 'support.faq',
            name: 'menu.faq',
            icon: 'helpCircle',
            element: <FaqsView />,
          },
          {
            path: 'support.contact',
            icon: 'messageSquare',
            name: 'menu.contact',
            element: <Outlet />,
            children: [
              {
                onlyRoute: true,
                index: true,
                path: 'support.contact',
                name: '',
                element: <ContactView />,
              },
              {
                onlyRoute: true,
                path: 'support.contact.closedTickets',
                name: '',
                element: <ContactView activePage="closedTickets" />,
              },
            ],
          },
        ],
      },
      {
        path: 'oma-annos',
        name: 'menu.omaAnnosService',
        icon: 'monitor',
        protection: {
          pharmacyOrCareUnit: true,
        },
        element: <Outlet />,
        children: [
          {
            path: 'oma-annos',
            name: 'menu.omaAnnosOrders',
            protection: {
              doseDispensingPharmacy: true,
              allowPharmacCustomerService: true,
              customerViewOrders: true,
            },
            element: <Outlet />,
            children: [
              {
                path: 'oma-annos.orders',
                name: 'menu.omaAnnosOrderHistory',
                icon: 'bookOpen',
                protection: {
                  pharmacy: true,
                  allowPharmacCustomerService: true,
                  customerViewOrders: true,
                },
                element: <OrderHistoryView />,
              },
              {
                path: 'oma-annos.orders.productShortages',
                name: 'menu.omaAnnosProductShortages',
                icon: 'fileText',
                protection: {
                  allowPharmacCustomerService: true,
                  customerViewOrders: true,
                  pharmacy: true,
                },
                element: <ProductShortagesView />,
              },
              {
                path: 'oma-annos.orders.tickets',
                name: 'menu.omaAnnosOrderTickets',
                icon: 'fileText',
                protection: {
                  allowPharmacCustomerService: true,
                  customerViewOrders: true,
                  pharmacy: true,
                },
                element: <OrderTicketsView />,
              },
              {
                path: 'oma-annos.orders.orderChanges',
                name: '',
                onlyRoute: true,
                protection: {
                  customerViewOrders: true,
                  allowPharmacCustomerService: true,
                },
                element: <OrderChangesView />,
              },
              {
                path: 'oma-annos.order-schedules',
                name: 'menu.omaAnnosOrderSchedules',
                protection: {
                  customerViewOrders: true,
                  allowPharmacCustomerService: true,
                },
                element: <OrderSchedulesView />,
              },
            ],
          },
          {
            path: 'oma-annos.dispensedProducts',
            name: 'menu.omaAnnosProducts',
            icon: 'fileText',
            protection: {
              pharmacyOrCareUnit: true,
            },
            element: <DoseDispensingCatalogView />,
          },
          {
            path: 'oma-annos.customerGroupManagement',
            name: 'menu.omaAnnosCustomerGroupManagement',
            icon: 'fileText',
            protection: {
              allowPharmacCustomerService: true,
              customerViewOrders: true,
              doseDispensingPharmacy: true,
            },
            element: <CustomerGroupsView />,
          },
          {
            path: 'oma-annos.customerGroupManagement.edit',
            onlyRoute: true,
            name: '',
            element: <CustomerGroupSettingsView />,
            protection: {
              customerViewOrders: true,
              doseDispensingPharmacy: true,
            },
          },
          {
            path: 'oma-annos.customership.reports',
            onlyRoute: true,
            name: '',
            element: <CustomerReportsView />,
            protection: {
              customerViewInvoices : true,
              doseDispensingPharmacy: true
            },
          },
          {
            path: 'oma-annos.customership',
            name: 'menu.omaAnnosCustomership',
            protection: {
              customerUserManagement: true,
            },
            element: <Outlet />,
            onlyRoute: true,
            children: [
              {
                index: true,
                path: 'oma-annos.customership',
                name: '',
                element: <CustomerView />,
              },
              {
                path: 'oma-annos.customership.users',
                name: '',
                element: <CustomerView activePage="users" />,
                protection: {
                  customerUserManagement: true,
                },
              },
              {
                path: 'oma-annos.customership.user',
                name: '',
                protection: {
                  customerUserManagement: true,
                },
                element: <CustomerView activePage="users" />,
              },
              {
                path: 'oma-annos.customership.inviteLinks',
                name: '',
                protection: {
                  customerUserManagement: true,
                },
                element: <CustomerInviteLinksView />,
              },
            ],
          },
        ],
      },
      {
        path: 'user',
        name: '',
        element: <UserView />,
        children: [
          {
            path: 'user.manage',
            name: '',
            element: <UpdateUserInfoForm />,
          },
        ],
      },
    ],
  },
  {
    path: 'logout',
    name: '',
    element: <LogoutView />,
  },
  {
    path: 'unauthorized',
    name: '',
    element: <UnauthorizedView />,
  },
  {
    path: 'wildcard',
    name: '',
    element: <NotFoundPageView />,
  },
]

export const ExtranetRouterProvider = () => {
  const userAuthorizationFlags = useAuthorizationFlags()

  const routes = useMemo(() => {
    const parseRoute = (language: 'fi' | 'sv' | 'en', route: ExtranetRouteObject): any => {
      if (route.path && route.path === 'wildcard') {
        const prefix = language !== 'fi' ? `/${language}/` : ''

        return {
          ...route,
          path: prefix + '*',
        }
      }

      const hasProtection = route.protection && Object.keys(route.protection).length > 0

      const routeElement = hasProtection ? (
        <AuthorizedRoot {...route.protection}>{route.element}</AuthorizedRoot>
      ) : (
        route.element
      )

      return {
        path: localizeLinkPath(language, route.path),
        index: !!route.index,
        element: routeElement,
        ...(route.children &&
          route.children.length > 0 && {
            children: (route.children || []).map((r) => parseRoute(language, r)),
          }),
      }
    }

    const appRoutes = extranetRoutes()

    const { fi, sv, en } = ['fi', 'sv', 'en'].reduce<ReducedRoutes>(
      (acc, lang) => {
        return {
          ...acc,
          [lang]: appRoutes.map((route) => parseRoute(lang as 'fi' | 'sv' | 'en', route)),
        }
      },
      { fi: [], en: [], sv: [] }
    )

    type ReducedRoutes = {
      fi: RouteObject[]
      en: RouteObject[]
      sv: RouteObject[]
    }

    const combinedRoutes: RouteObject[] = [...fi, ...sv, ...en]

    return combinedRoutes
  }, [userAuthorizationFlags])

  return <RouterProvider router={createBrowserRouter(routes)} />
}
